// План: расписание, календарь, предписание на день, нагрузка, разгрузка — типы /api/plan и запросы.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { TargetSet } from '@/pages/workouts/api'

export type PlanKind = 'strength' | 'cardio'
export type PlanStatus = 'planned' | 'done' | 'skipped' | 'missed'
export type CardioSport = 'run' | 'bike' | 'walk' | 'swim' | 'other'

export type Cardio = {
  sport: CardioSport
  duration_s: number | null
  distance_m: number | null
  hr_zone: number | null
}

export type Rule = {
  id: string
  weekday: number
  kind: PlanKind
  template_id: string | null
  template_name: string | null
  cardio: Cardio | null
  active: boolean
}

export type RuleInput = {
  id?: string | null
  weekday: number
  kind: PlanKind
  template_id: string | null
  cardio: Cardio | null
  active: boolean
}

export type Planned = {
  id: string
  day: string
  kind: PlanKind
  template_id: string | null
  template_name: string | null
  cardio: Cardio | null
  status: PlanStatus
  rule_id: string | null
  moved_from: string | null
  workout_session_id: string | null
  activity_id: string | null
  garmin_workout_id: number | null
  garmin_pushed_at: string | null
  garmin_stale: boolean
  warnings: string[]
}

export type CardioFact = {
  activity_id: string
  activity_name: string | null
  start_at: string
  planned_duration_s: number | null
  actual_duration_s: number | null
  planned_distance_m: number | null
  actual_distance_m: number | null
  hr_zone: number | null
  zone_time_s: number | null
}

/** Плановая глазами Garmin: можно ли отправить, что уйдёт шагом «другое», факт кардио. */
export type PlanGarmin = {
  planned: Planned
  connected: boolean
  can_push: boolean
  reason: string | null
  unmapped: string[]
  fact: CardioFact | null
}

export type TrainingStatus = { code: number | null; label: string; day: string | null }

export type PlannedInput = { day: string; kind: PlanKind; template_id: string | null; cardio: Cardio | null }

export type PrescribedExercise = {
  template_exercise_id: string
  position: number
  exercise_id: string
  exercise_name: string
  tracking: string
  sets: TargetSet[]
  weight_delta_kg: number
  reason: string | null
  pending_deload_id: string | null
  injury_warning: string | null
  notes: string | null
}

export type Prescription = {
  template_id: string
  template_name: string
  day: string
  exercises: PrescribedExercise[]
  warnings: string[]
}

export type Deload = {
  id: string
  template_exercise_id: string
  template_id: string | null
  template_name: string | null
  exercise_name: string | null
  current_weight_kg: number | null
  suggested_weight_kg: number
  status: 'pending' | 'accepted' | 'rejected'
  reason: string | null
  created_at: string
}

export type MuscleVolume = { muscle: string; label_ru: string; sets: number; tonnage_kg: number }

export type WeekVolume = {
  week_start: string
  sets: number
  tonnage_kg: number
  sessions: number
  muscles: MuscleVolume[]
  training_load: number | null
}

export type Volume = {
  week_start: string
  current: WeekVolume
  previous: WeekVolume[]
  average_sets: Record<string, number>
}

export const planKeys = {
  all: ['plan'] as const,
  rules: ['plan', 'rules'] as const,
  calendar: (start: string, end: string) => ['plan', 'calendar', start, end] as const,
  prescription: (templateId: string, day: string) => ['plan', 'prescription', templateId, day] as const,
  volume: (weekStart: string) => ['plan', 'volume', weekStart] as const,
  deloads: ['plan', 'deloads'] as const,
  garmin: (id: string) => ['plan', 'garmin', id] as const,
  trainingStatus: ['plan', 'training-status'] as const,
}

export const planApi = {
  rules: () => api.get<Rule[]>('/plan/rules'),
  setRules: (rules: RuleInput[]) => api.put<Rule[]>('/plan/rules', { rules }),
  calendar: (start: string, end: string) => api.get<Planned[]>('/plan/calendar', { query: { start, end } }),
  add: (data: PlannedInput) => api.post<Planned>('/plan/sessions', data),
  move: (id: string, day: string) => api.post<Planned>(`/plan/sessions/${id}/move`, { day }),
  skip: (id: string, skipped: boolean) => api.post<Planned>(`/plan/sessions/${id}/skip`, { skipped }),
  remove: (id: string) => api.delete(`/plan/sessions/${id}`),
  prescription: (templateId: string, day: string) =>
    api.get<Prescription>('/plan/prescription', { query: { template_id: templateId, day } }),
  volume: (weekStart: string) => api.get<Volume>('/plan/volume', { query: { week_start: weekStart } }),
  deloads: () => api.get<Deload[]>('/plan/deloads'),
  resolveDeload: (id: string, accept: boolean) => api.post<Deload>(`/plan/deloads/${id}/resolve`, { accept }),
  garmin: (id: string) => api.get<PlanGarmin>(`/plan/sessions/${id}/garmin`),
  pushGarmin: (id: string) => api.post<Planned>(`/plan/sessions/${id}/push-garmin`),
  trainingStatus: () => api.get<TrainingStatus>('/plan/training-status'),
}

export function useRules() {
  return useQuery({ queryKey: planKeys.rules, queryFn: planApi.rules })
}

export function useCalendar(start: string, end: string) {
  return useQuery({
    queryKey: planKeys.calendar(start, end),
    queryFn: () => planApi.calendar(start, end),
    placeholderData: (previous) => previous,
  })
}

export function usePrescription(templateId: string | null, day: string, enabled = true) {
  return useQuery({
    queryKey: planKeys.prescription(templateId ?? '', day),
    queryFn: () => planApi.prescription(templateId ?? '', day),
    enabled: enabled && Boolean(templateId),
  })
}

export function useVolume(weekStart: string) {
  return useQuery({
    queryKey: planKeys.volume(weekStart),
    queryFn: () => planApi.volume(weekStart),
    placeholderData: (previous) => previous,
  })
}

export function useDeloads() {
  return useQuery({ queryKey: planKeys.deloads, queryFn: planApi.deloads })
}

export function usePlanGarmin(id: string, enabled = true) {
  return useQuery({ queryKey: planKeys.garmin(id), queryFn: () => planApi.garmin(id), enabled })
}

export function useTrainingStatus() {
  return useQuery({ queryKey: planKeys.trainingStatus, queryFn: planApi.trainingStatus })
}

/** Мутация плана: после успеха обновляет всё, что зависит от плана. */
export function usePlanMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeys.all }),
  })
}
